import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { useI18n } from "../i18n";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { setLocale } = useI18n();
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setWorkspace(data.workspace);
      setSubscription(data.subscription);
      if (data.user?.locale) setLocale(data.user.locale);
      try {
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (data.workspace && data.user?.role !== "accountant" && browserTz && data.workspace.timezone !== browserTz) {
          await api.put("/settings", { timezone: browserTz });
          data.workspace.timezone = browserTz;
          setWorkspace({ ...data.workspace });
        }
      } catch (e) { console.warn("No se pudo sincronizar la zona horaria:", e); }
      return data;
    } catch (e) {
      setUser(null);
      setWorkspace(null);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    (async () => {
      await refreshMe();
      setLoading(false);
    })();
  }, [refreshMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    await refreshMe();
    return data;
  };

  const verifyMfa = async (code) => {
    const { data } = await api.post("/auth/mfa/verify", { code });
    await refreshMe();
    return data;
  };

  const register = async (payload) => {
    // A2: el registro no inicia sesion automaticamente (respuesta uniforme
    // anti-enumeracion). El usuario inicia sesion despues.
    const { data } = await api.post("/auth/register", payload);
    return data;
  };

  const demoLogin = async (profile, sector = "peluqueria", key = "") => {
    const { data } = await api.post("/demo/login", { profile, sector, key });
    await refreshMe();
    return data;
  };

  const setSessionToken = async () => {
    return await refreshMe();
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout request failed:", e); }
    setUser(null); setWorkspace(null); setSubscription(null);
  };

  return (
    <AuthContext.Provider value={{ user, workspace, subscription, loading, login, verifyMfa, register, demoLogin, logout, refreshMe, setSessionToken, setWorkspace, setSubscription }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { apiError };
